/**
 * One-time enrollment-code modal (ported from CC `EnrollModal.tsx`
 * onto the fetch fleet client).
 */
import { useState } from "react";
import {
  ActionIcon, Button, Code, CopyButton, Modal, Stack, Text, Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";

import { fleetApi } from "../../api/fleet";

interface EnrollModalProps {
  opened: boolean;
  onClose: () => void;
}

export function EnrollModal({ opened, onClose }: EnrollModalProps) {
  const [result, setResult] = useState<{ code: string; expires_at: string } | null>(null);

  const generate = useMutation({
    mutationFn: () => fleetApi.enrollment.createCode(),
    onSuccess: (data) => setResult(data),
    onError: () => notifications.show({ color: "red", message: "Failed to generate enrollment code." }),
  });

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  const enrollCommand = (code: string) =>
    `python -m pi_bot enroll --code ${code} --server https://gmvpcc.com`;

  return (
    <Modal opened={opened} onClose={handleClose} title="Enroll New Bot">
      <Stack>
        {!result ? (
          <>
            <Text size="sm" c="dimmed">
              Generate a one-time enrollment code. On the bot host machine (Pi), run:
            </Text>
            <Code block>{enrollCommand("<CODE>")}</Code>
            <Button onClick={() => generate.mutate()} loading={generate.isPending}>
              Generate Enrollment Code
            </Button>
          </>
        ) : (
          <>
            <Text size="sm">Share this code with the bot host. It expires in 24 hours.</Text>
            <Stack gap="xs">
              <Text size="xs" c="dimmed">Enrollment Code</Text>
              <Code block style={{ fontSize: 20, letterSpacing: 4 }}>{result.code}</Code>
              <CopyButton value={result.code} timeout={2000}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? "Copied!" : "Copy"} withArrow>
                    <ActionIcon color={copied ? "teal" : "blue"} onClick={copy}>
                      {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </Stack>
            <Stack gap="xs">
              <Text size="xs" c="dimmed">Run this on the bot host machine:</Text>
              <Code block>{enrollCommand(result.code)}</Code>
              <CopyButton value={enrollCommand(result.code)} timeout={2000}>
                {({ copied, copy }) => (
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                    color={copied ? "teal" : "blue"}
                    onClick={copy}
                  >
                    {copied ? "Copied!" : "Copy Command"}
                  </Button>
                )}
              </CopyButton>
            </Stack>
            <Text size="xs" c="dimmed">Expires: {new Date(result.expires_at).toLocaleString()}</Text>
            <Button variant="light" onClick={handleClose}>Done</Button>
          </>
        )}
      </Stack>
    </Modal>
  );
}
